import { getAllCities } from '../data/citiesCoordinates';

export interface NormalizedImportEvent {
  name: string;
  city: string;
  date: string;
  time: string;
  type: string;
  price: string;
  showPrice: boolean;
  address: string;
  link: string;
  description: string;
  multiDay: boolean;
  isMultiDay: boolean;
  endDate: string;
  organizer: string;
  phone: string;
  email: string;
}

const CITY_ALIASES: Record<string, string> = {
  Warsaw: 'Warszawa',
  Warszawa: 'Warszawa',
  Krakow: 'Kraków',
  Kraków: 'Kraków',
  Gdansk: 'Gdańsk',
  Gdańsk: 'Gdańsk',
  Poznan: 'Poznań',
  Poznań: 'Poznań',
  Wroclaw: 'Wrocław',
  Wrocław: 'Wrocław',
  Lodz: 'Łódź',
  Łódź: 'Łódź',
  Bialystok: 'Białystok',
  Białystok: 'Białystok',
  Rzeszow: 'Rzeszów',
  Rzeszów: 'Rzeszów',
  Torun: 'Toruń',
  Toruń: 'Toruń',
  Czestochowa: 'Częstochowa',
  Częstochowa: 'Częstochowa',
  'Zielona Gora': 'Zielona Góra',
  'Zielona Góra': 'Zielona Góra',
};

const APP_CITIES = getAllCities();

function stripDiacritics(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '');
}

function formatImportDate(value: unknown): string {
  if (!value) return '';
  return String(value).split('T')[0];
}

/** Data w strefie Europe/Warsaw (np. z utcStartDate scrapera FB). */
function toPolishDate(isoUtc: unknown): string {
  if (!isoUtc) return '';
  const date = new Date(String(isoUtc));
  if (Number.isNaN(date.getTime())) return formatImportDate(isoUtc);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw' }).format(date);
}

function toPolishTime(isoUtc: unknown): string {
  if (!isoUtc) return '';
  const date = new Date(String(isoUtc));
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Warsaw',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function normalizeCityName(rawCity: string): string {
  const trimmed = rawCity.trim();
  if (!trimmed) return '';

  if (CITY_ALIASES[trimmed]) return CITY_ALIASES[trimmed];

  const lower = stripDiacritics(trimmed).toLowerCase();
  for (const appCity of APP_CITIES) {
    if (stripDiacritics(appCity).toLowerCase() === lower) return appCity;
  }

  return trimmed;
}

function findCityInText(text: string): string {
  if (!text) return '';
  const hay = stripDiacritics(text).toLowerCase();

  // Dłuższe nazwy najpierw (np. "Zielona Góra" przed "Góra")
  const citiesByLength = [...APP_CITIES].sort((a, b) => b.length - a.length);
  for (const city of citiesByLength) {
    const needle = stripDiacritics(city).toLowerCase();
    const re = new RegExp(`(^|[^a-z0-9])${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`, 'i');
    if (re.test(hay)) return city;
  }
  return '';
}

function stripCountrySuffix(value: string): string {
  return value.replace(/,\s*(Poland|Polska)\s*$/i, '').trim();
}

/**
 * Facebook Events Scraper (Apify) używa płaskich kluczy "location.city", "location.name".
 * Fallback: szukaj miasta w nazwie miejsca / tytule wydarzenia.
 */
function pickCity(raw: Record<string, unknown>): string {
  const locCity = raw['location.city'] ?? (raw.location as { city?: unknown } | undefined)?.city;
  const locName = raw['location.name'] ?? (raw.location as { name?: unknown } | undefined)?.name;
  const name = raw.name;

  const candidates: string[] = [];

  if (typeof raw.city === 'string' && raw.city.trim()) {
    candidates.push(stripCountrySuffix(raw.city));
  }
  if (typeof locCity === 'string' && locCity.trim()) {
    candidates.push(stripCountrySuffix(locCity));
  }
  if (typeof locName === 'string' && locName.trim()) {
    const s = locName.trim();
    // np. "Długa 19, 85-034 Bydgoszcz, Poland"
    const withPostal = s.match(/\d{2}-\d{3}\s+([^,]+)/);
    if (withPostal) candidates.push(withPostal[1].trim());
    const lastSegment = s.split(',').map((p) => stripCountrySuffix(p.trim())).filter(Boolean);
    if (lastSegment.length) candidates.push(...lastSegment.reverse());
    candidates.push(s);
  }

  for (const candidate of candidates) {
    const normalized = normalizeCityName(candidate);
    if (APP_CITIES.includes(normalized)) return normalized;
  }

  const fromText = findCityInText(
    [locCity, locName, name].filter((v) => typeof v === 'string').join(' | ')
  );
  if (fromText) return fromText;

  // Ostatnia deska: surowa wartość city / location.city (nawet spoza listy filtrów)
  for (const candidate of candidates) {
    const normalized = normalizeCityName(candidate);
    if (normalized) return normalized;
  }

  return '';
}

function pickDate(raw: Record<string, unknown>): string {
  if (raw.date) return formatImportDate(raw.date);
  if (raw.utcStartDate) return toPolishDate(raw.utcStartDate);
  if (raw.startDate) return toPolishDate(raw.startDate);
  return '';
}

function pickTime(raw: Record<string, unknown>): string {
  if (typeof raw.time === 'string' && raw.time.trim()) return raw.time.trim();
  if (raw.utcStartDate) return toPolishTime(raw.utcStartDate);
  if (raw.startDate) return toPolishTime(raw.startDate);
  return '';
}

function pickLink(raw: Record<string, unknown>): string {
  return String(raw.link || raw.url || '').trim();
}

function pickAddress(raw: Record<string, unknown>): string {
  if (typeof raw.address === 'string' && raw.address.trim()) return raw.address.trim();
  const locName = raw['location.name'] ?? (raw.location as { name?: unknown } | undefined)?.name;
  return typeof locName === 'string' ? locName.trim() : '';
}

function pickOrganizer(raw: Record<string, unknown>): string {
  if (typeof raw.organizer === 'string' && raw.organizer.trim()) return raw.organizer.trim();
  if (typeof raw.organizedBy === 'string' && raw.organizedBy.trim()) {
    return raw.organizedBy.replace(/^Event by\s+/i, '').trim();
  }
  return '';
}

function pickMultiDay(raw: Record<string, unknown>): boolean {
  if (raw.multi_day ?? raw.multiDay) return true;
  if (raw.is_multi_day ?? raw.isMultiDay) return true;
  const duration = typeof raw.duration === 'string' ? raw.duration.toLowerCase() : '';
  return /\d+\s*days?/.test(duration);
}

/** Akceptuje: tablicę, { events: [] } (Live Nation), eksport snake_case z Postgres */
export function extractEventsArray(parsed: unknown): unknown[] | null {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { events?: unknown[] }).events)) {
    return (parsed as { events: unknown[] }).events;
  }
  return null;
}

export function normalizeImportedEvent(raw: Record<string, unknown>): NormalizedImportEvent {
  const source = raw.source as string | undefined;
  const showPriceRaw = raw.show_price !== undefined ? raw.show_price : raw.showPrice;
  const city = pickCity(raw);
  const date = pickDate(raw);
  const multiDay = pickMultiDay(raw);
  const organizer = pickOrganizer(raw);

  return {
    name: String(raw.name || '').trim(),
    city,
    date,
    time: pickTime(raw),
    type: (raw.type as string) || 'Koncert',
    price: (raw.price as string) || '',
    showPrice: showPriceRaw !== false,
    address: pickAddress(raw),
    link: pickLink(raw),
    description: (raw.description as string) || '',
    multiDay,
    isMultiDay: multiDay || !!(raw.is_multi_day ?? raw.isMultiDay),
    endDate: formatImportDate(raw.end_date ?? raw.endDate),
    organizer:
      organizer ||
      (source === 'livenation' ? 'Live Nation' : source === 'facebook' ? 'Facebook' : 'Użytkownik'),
    phone: (raw.phone as string) || '',
    email: (raw.email as string) || '',
  };
}

export function eventDedupKey(event: {
  name: string;
  city: string;
  date: string;
  link?: string;
}): string {
  const link = (event.link || '').trim().toLowerCase();
  if (link) return `link:${link}`;
  return `n:${event.name.trim().toLowerCase()}|${event.city.trim().toLowerCase()}|${formatImportDate(event.date)}`;
}

export function filterNewEvents(
  incoming: NormalizedImportEvent[],
  existing: Array<{ name: string; city: string; date: string; link?: string }>
): { toAdd: NormalizedImportEvent[]; skippedDuplicates: number } {
  const seen = new Set(
    existing.map((e) =>
      eventDedupKey({
        name: e.name,
        city: e.city,
        date: formatImportDate(e.date),
        link: e.link,
      })
    )
  );

  const toAdd: NormalizedImportEvent[] = [];
  let skippedDuplicates = 0;

  for (const event of incoming) {
    const key = eventDedupKey(event);
    if (seen.has(key)) {
      skippedDuplicates += 1;
      continue;
    }
    seen.add(key);
    toAdd.push(event);
  }

  return { toAdd, skippedDuplicates };
}

export function validateAndNormalizeImport(rawList: unknown[]): {
  valid: NormalizedImportEvent[];
  skipped: number;
} {
  const valid: NormalizedImportEvent[] = [];
  let skipped = 0;

  for (const item of rawList) {
    if (!item || typeof item !== 'object') {
      skipped += 1;
      continue;
    }
    const raw = item as Record<string, unknown>;
    const name = String(raw.name || '').trim();
    const city = pickCity(raw);
    const date = pickDate(raw);

    if (!name || !city || !date) {
      skipped += 1;
      continue;
    }

    valid.push(normalizeImportedEvent(raw));
  }

  return { valid, skipped };
}
